import { ComponentType, useCallback } from 'react';
import Accordion, {
  AccordionPanel,
  AccordionPanelContent,
  AccordionPanelHeader,
} from '@glu/accordion-react';
import { EyeLineNextIcon, FunnelNextIcon } from '@glu/icons-react';
import { Button, SMALL, TERTIARY } from '@glu/buttons-react';
import locale from '@glu/locale';
import { Typography } from '@glu/typography-react';
import DateRangeFilter from 'Filters/DateRangeFilter/DateRangeFilter';
import FuzzyFilter from 'Filters/FuzzyFilter/FuzzyFilter';
import DropdownFilter, { IDropdownFilterFilterable } from 'Filters/DropdownFilter/DropdownFilter';
import NumberFilter, { INumberFilterFilterable } from 'Filters/NumberFilter/NumberFilter';
import SelectFilter, { ISelectFilterFilterable } from 'Filters/SelectFilter/SelectFilter';
import ApplyCancelButtons from 'ApplyCancelButtons/ApplyCancelButtons';
import useStyles from './Filters.styles';

export interface ISelectOptions {
  /** The displayed label for the select option */
  label: string;
  /** The corresponding value for select option */
  value: string;
}

export interface IDateFilterValues {
  /**
   * This is required for the DateRangeFilter if a filter has
   * already been applied. It will represent the string value of
   * of the first date in range. Converted to moment object in component.
   */
  dateFrom: string;
  /**
   * Represents the string value of
   * of the later date in range. Converted to moment object in component.
   * If not provided but dateFrom is, dateTo will default to dateFrom value
   * to represent a single day rather than range.
   */
  dateTo: string;
}

export interface INumberFilterValues {
  /**
   * This is required for the NumberFilter if a filter has already been applied.
   * This will be the number to compare against. In the case of the
   * inRange operator this will be the minimum value.
   */
  filter: string;
  /**
   * This is required for the NumberFilter if filter type = 'inRange'
   * and a filter has already been applied.
   */
  filterTo?: string;
  /**
   * This is required for the NumberFilter if a filter has already been applied.
   * Determines how to evaluate the number filter.
   */
  type: string;
}

export interface IStringFilter {
  /** The value used to filter the rows. */
  filter: string;
}

export interface ISelectFilter extends IStringFilter {
  /** The label of the filter selected. Used by default in filterChips */
  label: string;
}

export interface IDropdownFilter {
  /** A list of values to filter the rows with. */
  values: string[];
  /** A list of labels associated with the filters. */
  labels: string[];
}

export type TFilter =
  | IStringFilter
  | IDropdownFilter
  | IDateFilterValues
  | INumberFilterValues
  | { [key: string]: any };

export type TCustomFilterProps = {
  filterable: IFilterable;
  value?: TFilter;
  onChange: IFiltersProps['onFilterChange'];
};

interface IParseFilterValue {
  event: any;
  filterable: IFilterable;
  input?: string;
  value?: INumberFilterValues;
}

export interface IFilterable {
  /**
   * The name of the filterable element(Column in case of grids). Used to compare, set, and clear filters.
   */
  field: string;
  /**
   * The name the filter header will display.
   */
  headerName: string;
  /**
   * Used to determine if the column is hidden in the grid.
   * If true the filter head will display HIDDEN component.
   */
  hide: boolean;
  /**
   * Used to determine which filter component will render.
   * date => DateRangeFilter
   * select => SelectFilter
   * number => NumberFilter
   * dropdown => DropdownFilter
   * text || undefined => FuzzyFilter (FuzzyFilter is the default)
   */
  componentType?: string;
  /** Component to use to display the filter instead of the default */
  filter?: ComponentType<TCustomFilterProps>;
  /**
   * An object used to provide optional display options for different filter components.
   * Passed in the column definition for Ag-Grid implementation.
   * Note that this option is required for select and dropdown filters in order to get options
   */
  filterOptions?: {
    /**
     * This is required for the SelectFilter and DropdownFilter.
     * Provides the selectable options for the filter.
     */
    getOptions?(...args: any[]): Promise<any>;
    /**
     * Pass to NumberFilter to override number filter type options.
     * Examples would be equals, greater than etc. All options provided
     * by AG-Grid are in the default; equals, notEqual, lessThan, lessThanOrEqual,
     * greaterThan, greaterThanOrEqual, inRange
     */
    numberFilters?: ISelectOptions[];
    /**
     * If true, NumberFilter 'inRange' will include values equal to start and end of range.
     */
    inRangeInclusive?: boolean;
    /** Props to be spread onto the UI component used to render the filter. */
    componentProps?: {
      [key: string]: any;
    };
    /** Allows consumer to parse and return any filter value from the filter component. */
    parseFilterValue?(params: IParseFilterValue): any;
    /** Allows any additional options to be passed for custom filters. */
    [key: string]: any;
  };
}

export interface IOnFilterChangeProps {
  /** The filterable element to which the change was made. */
  filterable: IFilterable;
  /** The new value of the filter. */
  value: TFilter;
}
export interface IFiltersProps {
  /**
   * This is required if a filter has already been applied.
   * The key, aka field, is the field to be filtered. The value
   * should be the corresponding TFilter type for that type of filter.
   */
  filterValues?: {
    [field: string]: TFilter;
  };
  /**
   * A list of filterable elements, like a column, that is mapped through
   * to create filters in the filter drawer.
   */
  filterables: IFilterable[];
  /**
   * The function used to apply any filters created or removed in the filter drawer.
   */
  onApply(): void;
  /**
   * The function used to remove all local filters and maintain
   * the filters that are currently applied.
   */
  onCancel(): void;
  /**
   * The function used to remove a filter from a particular field.
   */
  onClear(field: string): void;
  /**
   * The function used to pass filter values as
   * they are updated in the filter drawer.
   */
  onFilterChange(params: IOnFilterChangeProps): void;
  /**
   * If the name of a field is contained in this array and that field has a
   * filter then the filter's accordion will be open.
   */
  openFilterFields?: string[];
}

export interface IFilterProps {
  onChange: IFiltersProps['onFilterChange'];
  filterable: IFilterable;
}

export const getFilterComponent = ({
  filterValue,
  filterable,
  onFilterChange,
}: {
  filterable: IFilterable;
  filterValue?: TFilter;
  onFilterChange: IFiltersProps['onFilterChange'];
}) => {
  const { componentType, filter: CustomFilter } = filterable;
  if (typeof CustomFilter === 'function') {
    return <CustomFilter filterable={filterable} onChange={onFilterChange} value={filterValue} />;
  }
  switch (componentType) {
    case 'date':
      return (
        <DateRangeFilter
          filterable={filterable}
          onChange={onFilterChange}
          value={filterValue as IDateFilterValues}
        />
      );
    case 'select':
      return (
        <SelectFilter
          filterable={filterable as ISelectFilterFilterable}
          onChange={onFilterChange}
          value={filterValue as ISelectFilter}
        />
      );
    case 'dropdown':
      return (
        <DropdownFilter
          filterable={filterable as IDropdownFilterFilterable}
          onChange={onFilterChange}
          value={filterValue as IDropdownFilter}
        />
      );
    case 'number':
      return (
        <NumberFilter
          filterable={filterable as INumberFilterFilterable}
          onChange={onFilterChange}
          value={filterValue as INumberFilterValues}
        />
      );
    case 'text':
    default:
      return (
        <FuzzyFilter
          filterable={filterable}
          onChange={onFilterChange}
          value={filterValue as IStringFilter}
        />
      );
  }
};

function FiltersComponent({
  filterValues,
  filterables,
  onApply,
  onCancel,
  onClear,
  onFilterChange,
  openFilterFields,
}: IFiltersProps) {
  const classes = useStyles();
  const getComponent = useCallback(
    (filterable: IFilterable) => {
      const filterValue = filterValues?.[filterable.field];
      return getFilterComponent({ filterable, filterValue, onFilterChange });
    },
    [filterValues, onFilterChange]
  );

  return (
    <>
      <Accordion className={classes.filterableWrapper}>
        {filterables.map((filterable) => {
          const { field, headerName, hide } = filterable;
          const hasFilter = !!filterValues?.[field];
          return (
            <AccordionPanel key={field} isOpen={openFilterFields?.includes(field)} panelId={field}>
              <div className={classes.header}>
                <AccordionPanelHeader className={classes.panelHeader}>
                  <Typography className={classes.panelHeaderMessage}>{headerName}</Typography>
                </AccordionPanelHeader>
                {hasFilter && (
                  <div className={classes.clearFilter}>
                    <FunnelNextIcon noIconWrapper />
                    <Button
                      className={classes.clearBtn}
                      onClick={() => onClear(field)}
                      size={SMALL}
                      text={locale.get('dataComponents.clear')}
                      variant={TERTIARY}
                    />
                  </div>
                )}
                {hide && (
                  <div className={classes.hiddenField}>
                    <EyeLineNextIcon noIconWrapper />
                    <span>{locale.get('dataComponents.hidden')}</span>
                  </div>
                )}
              </div>
              <AccordionPanelContent className={classes.accordionContent}>
                {getComponent(filterable)}
              </AccordionPanelContent>
            </AccordionPanel>
          );
        })}
      </Accordion>
      <ApplyCancelButtons onApply={onApply} onCancel={onCancel} />
    </>
  );
}

export default FiltersComponent;
